<template>
  <div class="user-detail-component" v-if="selectedUser">
    <header></header>
    <b-tabs class="has-hidden-tabs has-no-padding" v-model="activeTab">
      <b-tab-item>
        <div class="user-detail-image">
          <span class="user-detail-circle">
            <h1 class="user-detail-initials">
              {{ userNameInitials }}
            </h1>
          </span>
          <span class="close-user-detail" @click="$emit('close')">
            <font-awesome-icon :icon="['far', 'times-circle']" size="2x"></font-awesome-icon>
          </span>
        </div>
        <h5 class="has-text-centered">
          <strong>{{ selectedUser.firstName }} {{ selectedUser.lastName }}</strong>
        </h5>
        <h6 class="has-text-centered">{{ selectedUser.email }}</h6>
        <span class="user-section-divider"></span>
        <main>
          <b-field label="Contact">
            {{ selectedUser.email }}
          </b-field>
          <b-field label="Account">
            {{ selectedUser.accountName }}
          </b-field>
          <b-field label="Role">
            {{ selectedUser.roleName }}
          </b-field>
          <b-field label="Description">
            {{ selectedUser.description || "None set" }}
          </b-field>
          <b-field label="Login Type">
            {{ selectedUser.loginType == "ThirdParty" ? "3rd Party" : "Email/Password" }}
          </b-field>
          <b-field label="Last Login">
            {{ selectedUser.lastLogin ? format(new Date(selectedUser.lastLogin), "yyyy-MM-dd hh:mm a' UTC'") : "Never" }}
          </b-field>
          <b-field label="Enabled">
            {{ selectedUser.enabled ? "Yes" : "No" }}
          </b-field>
          <restricted-wrapper
            label="Kymeta Admin"
            type="is-warning"
            v-if="$hasRole('Super User,Kymeta Admin')"
            style="margin-top: 1.25rem"
          >
            <b-field label="User Id">
              {{ selectedUser.id }}
            </b-field>
            <b-field label="Created On">
              {{ selectedUser.createdOn ? format(new Date(selectedUser.createdOn), "yyyy-MM-dd hh:mm a' UTC'") : "Never" }}
            </b-field>
          </restricted-wrapper>
        </main>
        <span class="user-section-divider"></span>
        <main>
          <label class="label">Actions</label>
          <div class="buttons">
            <b-button @click="toggleEdit" icon-left="edit" type="is-primary" size="is-small" v-if="$hasRole('Super User,Kymeta Admin,Account Admin')">Edit User</b-button>
            <b-button @click="activeTab = 1" icon-left="list-ul" type="is-primary" size="is-small">View Activity</b-button>
            <b-button @click="resendInvitation" title="Available only when user has never logged in and login type is Local" icon-left="envelope-open" type="is-primary" size="is-small" v-if="!selectedUser.lastLogin && selectedUser.loginType == 'Local'">Resend Invitation</b-button>
          </div>
        </main>
      </b-tab-item>
      <b-tab-item>
        <main>
          <div class="level">
            <div class="level-left">
              <font-awesome-icon class="level-item" :icon="['far', 'chevron-left']" size="sm"></font-awesome-icon> <a class="level-item" @click="activeTab = 0">Return to User Detail</a>
            </div>
          </div>
          <label class="label">Recent Activity</label>
          <activity-log-item
            :model="item"
            v-for="(item, idx) in activities"
            :key="idx"
            :exclude-name="true"
          ></activity-log-item>
          <p v-if="!fetchingUser && !activities.length">No activities found.</p>
          <p v-if="fetchingUser">Retrieving last 10 activities...</p>
        </main>
      </b-tab-item>
      <b-tab-item>
        <main v-if="clonedUser">
          <b-field label="First Name">
            <b-input v-model="clonedUser.firstName" />
          </b-field>
          <b-field label="Last Name">
            <b-input v-model="clonedUser.lastName" />
          </b-field>
          <b-field label="Account">
            <b-select expanded v-model="clonedUser.accountId" @input="nullOutRole">
              <option :value="account.id" v-for="account in accounts" :key="account.id">{{account.name}}</option>
            </b-select>
          </b-field>
          <b-field label="Role">
            <b-select expanded v-model="clonedUser.roleId" @input="updateRoleName">
              <option :value="role.id" v-for="role in roles" :key="role.id + '_100'">{{role.name}}</option>
            </b-select>
          </b-field>
          <b-field label="Login Type" required>
            <b-select expanded v-model="clonedUser.loginType">
              <option value="ThirdParty">Third Party (Google, Microsoft, Apple)</option>
              <option value="Local">Local (Email/Password)</option>
            </b-select>
          </b-field>
          <b-field label="Description">
            <b-input type="textarea" rows="6" v-model="clonedUser.description" />
          </b-field>
          <div class="buttons is-right">
            <b-button type="is-primary" icon-left="save" @click="updateUser" :disabled="isUpdatingUser" :loading="isUpdatingUser">Save</b-button>
            <b-button type="is-outlined" @click="activeTab = 0; clonedUser = null;" :disabled="isUpdatingUser">Cancel</b-button>
          </div>
        </main>
      </b-tab-item>
    </b-tabs>
  </div>
</template>
<script>
import { format } from 'date-fns';
import { orderBy } from 'lodash';
import RestrictedWrapper from '../../shared/RestrictedWrapperComponent.vue';
import ActivityLogItem from '../../shared/ActivityLogItem.vue';

export default {
  props: ['selectedUser'],
  components: {
    RestrictedWrapper,
    ActivityLogItem,
  },
  computed: {
    userNameInitials() {
      return `${this.selectedUser.firstName[0]}${
        this.selectedUser.lastName ? this.selectedUser.lastName[0] : ''
      }`;
    },
    activities() {
      return this.$store.state.admin.users.user?.activities || [];
    },
    roles() {
      return orderBy(this.$store.state.admin.roles.roles, ['name'], ['asc']);
    },
    accounts() {
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    }
  },
  data() {
    return {
      activeTab: 0,
      fetchingUser: false,
      clonedUser: null,
      isUpdatingUser: false,
      format,
    };
  },
  watch: {
    // eslint-disable-next-line
    selectedUser: async function (newValue) {
      if (newValue != null) {
        this.fetchingUser = true;
        await this.$store.dispatch('fetchUser', { id: this.selectedUser.id });
        this.fetchingUser = false;
      }
    },
  },
  methods: {
    toggleEdit() {
      this.clonedUser = { ...this.selectedUser };
      this.activeTab = 2;
    },
    async updateUser() {
      if (!this.clonedUser.roleId) {
        this.$buefy.toast.open({
          type: 'is-danger',
          position: 'is-bottom-right',
          message: 'Please select a role for this account.'
        });
        return;
      }
      
      const objectToSend = { ...this.clonedUser }
      this.isUpdatingUser = true;
      const result = await this.$store.dispatch('updateUser', objectToSend);
      this.isUpdatingUser = false;
      if (result) {
        this.activeTab = 0;
        this.clonedUser = null;
        this.$emit('reselect', objectToSend);
      }
    },
    async resendInvitation() {
      this.$buefy.dialog.confirm({
        title: 'Resend Invitation',
        message: `Are you sure you want to regenerate and resend the invitation code to the following user: ${this.selectedUser.email}`,
        cancelText: 'Cancel',
        confirmText: 'Send Invitation',
        type: 'is-info',
        onConfirm: async () => {
          const result = await this.$store.dispatch('regenerateInviteCode', { id: this.selectedUser.id });
          if (result) {
            this.$buefy.toast.open({
              duration: 3000,
              message: 'Invitation sent successfully.',
              position: 'is-bottom-right',
              type: 'is-success'
            })
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: 'There was a problem regenerating the invite code.',
              position: 'is-bottom-right',
              type: 'is-danger'
            })
          }
        }
      })
    },
    nullOutRole(value) {
      const first = this.roles[0];
      this.clonedUser.accountName = this.accounts.find(a => a.id == value)?.name;
      this.clonedUser.roleId = first?.id;
      this.clonedUser.roleName = first?.name;
    },
    updateRoleName(value) {
      this.clonedUser.roleName = this.roles.find(r => r.id == value)?.name;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../styles/colors";

.user-detail-component {
  min-width: 360px;
  max-width: 640px;
  background: white;

  header {
    background: $blue;
    height: 0.25rem;
  }

  .user-detail-image {
    padding: 2rem;
    display: grid;
    place-items: center;
    position: relative;

    .user-detail-circle {
      border-radius: 50%;
      border: 1px solid $grey-lighter;
      display: grid;
      place-items: center;
      padding: 2rem;
      letter-spacing: 0;
      
      .user-detail-initials {
        min-width: 60px;
        min-height: 60px;
        display: grid;
        place-items: center;
      }
    }
  }

  .user-section-divider {
    margin: 1rem 45%;
    border-bottom: 4px solid $blue;
    display: block;
    height: 4px;
  }

  main {
    padding: 1rem;
  }

  .close-user-detail {
    position: absolute;
    top: 1rem;
    right: 1rem;
    cursor: pointer;
  }
}
</style>
